import {
	type ApiErrorResponse,
	type FetchIamMeTopUpRequirementsData,
	type FetchUserMeUserRequirementStatesV2Data,
	type FetchUserRequirementsUserMePvpData,
	type FetchUserRequirementUserMeRequirementIdentifierByRequirementIdentifierParams,
	type FetchUserUserRequirementStateUserMeRequirementIdentifierByRequirementIdentifierData,
	noop,
	type PostUserRequirementMobileGenerateCodeData,
	type PostUserRequirementMobileGenerateCodePayload,
	type PutUserUserRequirementStateMeMobileRequirementData,
	type PutUserUserRequirementStateUserMeRequirementIdentifierByRequirementIdentifierData,
	type RequirementIdentifier,
	userApi,
	type UserMobileRequirementUserProvidedValues,
	type UserProvidedValues,
	type UserRequirementState,
	type UserRequirementStateNew,
	type UserRequirementStatePvp
} from '@capsicum/common';
import { RequirementCompletionEventsEnum, useTrackRequirementsEvents, useUser } from '@capsicum/socios-core';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useFormErrorInterceptor } from 'libs/common/hooks/forms';
import { getResponseStatusIfAxiosError, iamApi, requirementApi } from 'libs/common/services/api';

import { customRequirementsAtom } from '../atoms';

import type { AxiosError } from 'axios';

const UseGetRequirementsQueryKey = ['GetRequirements'];

export const useGetRequirementsQuery = () => {
	return useQuery<FetchUserRequirementsUserMePvpData, unknown, Array<UserRequirementStatePvp>>({
		queryFn: async () => (await userApi).fetchUserRequirementsUserMePvp({ 'details[]': ['pvpTranslations'] }),
		queryKey: UseGetRequirementsQueryKey,
		select: data => data.data
	});
};

const UseGetRequirementByIdentifierQueryKey = ['GetRequirementByIdentifier'];

export const useGetRequirementByIdentifierQuery = (
	params: FetchUserRequirementUserMeRequirementIdentifierByRequirementIdentifierParams
) =>
	useQuery({
		queryFn: async () =>
			(await userApi).fetchUserRequirementUserMeRequirementIdentifierByRequirementIdentifier(params),
		queryKey: [UseGetRequirementByIdentifierQueryKey, params],
		refetchOnWindowFocus: false,
		select: res => res.data
	});

const UseGetRequirementStateByIdentifierQueryKey = ['GetRequirementStateByIdentifier'];

type UseGetRequirementStateByIdentifierQueryPayload = {
	requirementIdentifier: RequirementIdentifier;
};

export const useGetRequirementStateByIdentifierQuery = (
	params: UseGetRequirementStateByIdentifierQueryPayload,
	options?: Omit<
		UseQueryOptions<
			FetchUserUserRequirementStateUserMeRequirementIdentifierByRequirementIdentifierData,
			unknown,
			UserRequirementState
		>,
		'queryFn' | 'queryKey'
	>
) =>
	useQuery({
		queryFn: async () =>
			(await userApi).fetchUserUserRequirementStateUserMeRequirementIdentifierByRequirementIdentifier(
				params.requirementIdentifier
			),
		queryKey: [UseGetRequirementStateByIdentifierQueryKey, params.requirementIdentifier],
		refetchOnWindowFocus: false,
		select: res => res.data,
		...options
	});

const UseSendCodeVerificationBySMSQueryKey = ['send-code-verification-to-mobile'];

export const useSendCodeVerificationBySMS = (onSuccess = noop) => {
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation<
		PostUserRequirementMobileGenerateCodeData,
		AxiosError<ApiErrorResponse>,
		PostUserRequirementMobileGenerateCodePayload
	>({
		mutationFn: async (payload: PostUserRequirementMobileGenerateCodePayload) =>
			(await requirementApi).postUserRequirementMobileGenerateCode(payload, {
				customErrorInterceptor: e => getResponseStatusIfAxiosError(e) === 429
			}),
		mutationKey: UseSendCodeVerificationBySMSQueryKey,
		onSuccess
	});

	return {
		data,
		error,
		isLoading: isPending,
		isSuccess,
		mutateAsync
	};
};

const UseUpdateRequirementToWaitingByIdentifierQueryKey = ['UpdateRequirementToWaitingByIdentifier'];

type UseUpdateRequirementToWaitingByIdentifierQueryProps = {
	requirementIdentifier: RequirementIdentifier;
};

export const useUpdateRequirementToWaitingByIdentifierQuery = () => {
	const { trackRequirementsCompletion } = useTrackRequirementsEvents();
	const handleCustomRequirements = useSetAtom(customRequirementsAtom);

	const { data, error, isPending, isSuccess, mutateAsync } = useMutation({
		mutationFn: async ({ requirementIdentifier }: UseUpdateRequirementToWaitingByIdentifierQueryProps) =>
			(await userApi).putUserUserRequirementStateUserMeRequirementIdentifierWaitingByRequirementIdentifier(
				requirementIdentifier
			),
		mutationKey: UseUpdateRequirementToWaitingByIdentifierQueryKey,
		onError: (_error, { requirementIdentifier }) =>
			trackRequirementsCompletion(RequirementCompletionEventsEnum.failed, requirementIdentifier),
		onSuccess: (_data, { requirementIdentifier }) => {
			handleCustomRequirements({ type: 'INCREMENT' });
			trackRequirementsCompletion(RequirementCompletionEventsEnum.done, requirementIdentifier);
		}
	});

	return {
		data,
		error,
		isLoading: isPending,
		isSuccess,
		mutateAsync
	};
};

const UseSubmitRequirementStateByIdentifierQueryKey = ['SubmitRequirementStateByIdentifier'];

type UseSubmitRequirementStateByIdentifierQueryProps = {
	requirementData: UserProvidedValues;
	requirementIdentifier: RequirementIdentifier;
};

export const useSubmitRequirementStateByIdentifierQuery = ({
	enableFormErrorInterceptor,
	shouldNotRefetchCredentialsAtom
}: {
	enableFormErrorInterceptor?: boolean;
	shouldNotRefetchCredentialsAtom?: boolean;
} = {}) => {
	const { refetchUser } = useUser();
	const { formErrorInterceptor } = useFormErrorInterceptor();
	const customErrorInterceptor = enableFormErrorInterceptor ? formErrorInterceptor : () => false;
	const { trackRequirementsCompletion } = useTrackRequirementsEvents();
	const handleCustomRequirements = useSetAtom(customRequirementsAtom);

	const { data, error, isPending, isSuccess, mutate } = useMutation<
		PutUserUserRequirementStateUserMeRequirementIdentifierByRequirementIdentifierData,
		AxiosError<ApiErrorResponse>,
		UseSubmitRequirementStateByIdentifierQueryProps
	>({
		mutationFn: async ({
			requirementData,
			requirementIdentifier
		}: UseSubmitRequirementStateByIdentifierQueryProps) =>
			(await userApi).putUserUserRequirementStateUserMeRequirementIdentifierByRequirementIdentifier(
				requirementIdentifier,
				requirementData,
				{
					customErrorInterceptor
				}
			),
		mutationKey: UseSubmitRequirementStateByIdentifierQueryKey,
		onError: (_error, { requirementIdentifier }) =>
			trackRequirementsCompletion(RequirementCompletionEventsEnum.failed, requirementIdentifier),
		onSuccess: (_data, { requirementIdentifier }) => {
			trackRequirementsCompletion(RequirementCompletionEventsEnum.done, requirementIdentifier);
			handleCustomRequirements({ type: 'INCREMENT' });
			if (shouldNotRefetchCredentialsAtom) return;
			refetchUser();
		}
	});

	return {
		data,
		error,
		isLoading: isPending,
		isSuccess,
		mutate
	};
};

const UseSetMobileVerificationRequirementStateQueryKey = ['PutMobileVerificationRequirementState'];

export const useSetMobileVerificationRequirementStateQuery = (onSuccess = noop) => {
	const { trackRequirementsCompletion } = useTrackRequirementsEvents();
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation<
		PutUserUserRequirementStateMeMobileRequirementData,
		AxiosError<ApiErrorResponse>,
		UserMobileRequirementUserProvidedValues
	>({
		mutationFn: async (payload: UserMobileRequirementUserProvidedValues) =>
			(await userApi).putUserUserRequirementStateMeMobileRequirement(payload, {
				customErrorInterceptor: e => getResponseStatusIfAxiosError(e) === 400
			}),
		mutationKey: UseSetMobileVerificationRequirementStateQueryKey,
		onError: () => trackRequirementsCompletion(RequirementCompletionEventsEnum.failed, 'mobile_verification'),
		onSuccess: () => {
			trackRequirementsCompletion(RequirementCompletionEventsEnum.done, 'mobile_verification');
			onSuccess();
		}
	});

	return {
		data,
		error,
		isLoading: isPending,
		isSuccess,
		mutateAsync
	};
};

const UseGetRequirementCategoriesByIdentifierQueryKey = ['GetRequirementCategoriesByIdentifier'];

type UseGetRequirementCategoryByIdentifierQueryPayload = {
	requirementIdentifier: RequirementIdentifier;
};

export const useGetRequirementCategoriesByIdentifierQuery = (
	params: UseGetRequirementCategoryByIdentifierQueryPayload
) =>
	useQuery({
		queryFn: async () =>
			(await userApi).fetchUserUserRequirementDocumentCategoriesRequirementIdentifierByRequirementIdentifier(
				params.requirementIdentifier
			),
		queryKey: [UseGetRequirementCategoriesByIdentifierQueryKey, params.requirementIdentifier],
		refetchOnWindowFocus: false,
		select: res => res.data
	});

const UseGetForcedTopUpRequirementsQueryKey = ['GetForcedTopUpRequirements'];

export const useGetForcedTopUpRequirementsQuery = (enabled = false) =>
	useQuery<FetchIamMeTopUpRequirementsData, unknown, Array<RequirementIdentifier> | null>({
		enabled,
		queryFn: async () => (await iamApi).fetchIamMeTopUpRequirements(),
		queryKey: UseGetForcedTopUpRequirementsQueryKey,
		refetchOnWindowFocus: false,
		select: data => data.data
	});

const UseGetRequirementStatusesQueryKey = ['GetRequirementStatuses'];

export const useGetRequirementStatusesQuery = () =>
	useQuery<FetchUserMeUserRequirementStatesV2Data, unknown, Array<UserRequirementStateNew> | undefined>({
		queryFn: async () => (await userApi).fetchUserMeUserRequirementStatesV2({}),
		queryKey: UseGetRequirementStatusesQueryKey,
		refetchOnWindowFocus: false,
		select: data => data.data
	});
